// The reply a recruiter has started to a thread, kept while they look at another one (T296a), and
// what became of one they sent (T296b).
//
// In memory, never storage: a candidate's words stay off the disk, and a reload or a sign-out
// clears it. Keyed by conversation id, so a contact's thread is one reply for everyone put to them.
//
// A send (`sendKeptReply`) drops its draft BEFORE it is made (the box is unmounted by a move to
// another thread, and a mounted-only callback would never run) and keeps the text as IN FLIGHT, so
// a box drawn again before the answer shows what is on its way. `useSendMessage` settles it: a
// success forgets the text, a refusal restores it over nothing typed since and keeps the error for
// the box that is drawn when it lands, which may not be the one that sent.
//
// An external store, read with `useSyncExternalStore` (`subscribeReplies`, `repliesVersion`).
const drafts = new Map<string, string>();
const inFlight = new Map<string, string>();
const refusals = new Map<string, unknown>();
// Which sign-out era a kept send was made in: an answer from an earlier era belongs to nobody here.
let era = 0;
const sentIn = new Map<string, number>();

const listeners = new Set<() => void>();
let version = 0;
const changed = (): void => {
  version += 1;
  for (const listener of listeners) listener();
};

export function subscribeReplies(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A snapshot for `useSyncExternalStore`: changes whenever any reply, send or refusal does. */
export const repliesVersion = (): number => version;

export const draftOf = (conversationId: string): string => drafts.get(conversationId) ?? '';

/** The text of a send that has been made and not answered, or `null`. */
export const sendingOf = (conversationId: string): string | null =>
  inFlight.get(conversationId) ?? null;

/**
 * What the last refused send on this thread said, until the next send on that thread or a sign-out
 * (`clearReplies`); `null` if none.
 */
export const refusalOf = (conversationId: string): unknown => refusals.get(conversationId) ?? null;

/** An empty text deletes: the map holds only replies that exist. */
export function keepDraft(conversationId: string, text: string): void {
  if (text === '') drafts.delete(conversationId);
  else drafts.set(conversationId, text);
  changed();
}

export const dropDraft = (conversationId: string): void => {
  drafts.delete(conversationId);
  changed();
};

export function restoreDraft(conversationId: string, text: string): void {
  if (!drafts.has(conversationId)) keepDraft(conversationId, text);
}

/** The send was answered: nothing is on its way any more. */
export function settleSend(conversationId: string): void {
  sentIn.delete(conversationId);
  inFlight.delete(conversationId);
  changed();
}

/**
 * The send was refused: the text comes back over nothing newer, with what the service said. Not if
 * a sign-out came while it was on its way: the next person at this browser must not be handed it.
 */
export function refuseSend(conversationId: string, text: string, error: unknown): void {
  const madeIn = sentIn.get(conversationId);
  sentIn.delete(conversationId);
  if (madeIn !== undefined && madeIn !== era) return;
  inFlight.delete(conversationId);
  restoreDraft(conversationId, text);
  refusals.set(conversationId, error);
  changed();
}

/**
 * The one way the box sends: the kept reply is dropped and held as in flight, a past refusal goes,
 * then `send` is made with the same input.
 */
export function sendKeptReply<R>(
  input: { conversationId: string; body: string },
  send: (input: { conversationId: string; body: string }) => R,
): R {
  inFlight.set(input.conversationId, input.body);
  sentIn.set(input.conversationId, era);
  refusals.delete(input.conversationId);
  dropDraft(input.conversationId);
  return send(input);
}

/** Everything goes: for a sign-out (the next person at this browser must not see a reply) and tests. */
export function clearReplies(): void {
  era += 1;
  drafts.clear();
  inFlight.clear();
  refusals.clear();
  changed();
}
